import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import ConfirmModal from './ConfirmModal';
import { API_URL } from '../config';
import './Dashboard.css';

function Dashboard() {
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const { getAuthHeader } = useAuth();

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/admin/all`, {
        headers: getAuthHeader()
      });

      const posts = response.data;
      const published = posts.filter(p => p.status === 'published').length;
      const drafts = posts.filter(p => p.status === 'draft').length;

      setStats({
        totalPosts: posts.length,
        publishedPosts: published,
        draftPosts: drafts
      });

      setRecentPosts(posts.slice(0, 5));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    try {
      await axios.delete(`${API_URL}/posts/${postToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Post deleted successfully');
      setShowDeleteModal(false);
      setPostToDelete(null);
      fetchDashboardData(); // Refresh the dashboard
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
      setShowDeleteModal(false);
      setPostToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPostToDelete(null);
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard main-content">
      <div className="container">
        <h1 className="page-title">Dashboard</h1>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{backgroundColor: '#ff6b35'}}>
              <i className="icon">📝</i>
            </div>
            <div className="stat-info">
              <h3>{stats.totalPosts}</h3>
              <p>Total Posts</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{backgroundColor: '#28a745'}}>
              <i className="icon">✓</i>
            </div>
            <div className="stat-info">
              <h3>{stats.publishedPosts}</h3>
              <p>Published</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{backgroundColor: '#ffc107'}}>
              <i className="icon">📄</i>
            </div>
            <div className="stat-info">
              <h3>{stats.draftPosts}</h3>
              <p>Drafts</p>
            </div>
          </div>
        </div>

        <div className="recent-posts-section">
          <div className="section-header">
            <h2>Recent Posts</h2>
            <Link to="/posts" className="view-all-link">View All →</Link>
          </div>

          {recentPosts.length === 0 ? (
            <div className="card">
              <p style={{textAlign: 'center', color: '#666'}}>
                No posts yet. <Link to="/posts/new">Create your first post</Link>
              </p>
            </div>
          ) : (
            <div className="posts-list">
              {recentPosts.map(post => (
                <Link 
                  key={post.id} 
                  to={`/posts/edit/${post.id}`} 
                  className="post-item post-item-link"
                >
                  <div className="post-info">
                    <h3>{post.title}</h3>
                    <div className="post-meta">
                      <span className={`status-badge ${post.status}`}>
                        {post.status}
                      </span>
                      <span>{post.category}</span>
                      <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Post"
        message={`Are you sure you want to delete "${postToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}

export default Dashboard;
